import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRef } from 'react';
import axios from 'axios';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Constants } from '../lib/const/constants';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MdDownloading } from 'react-icons/md';
import { HiOutlineArrowDownTray } from 'react-icons/hi2';
import { getCurrencySymbol } from '../lib/currenyUtils';
import CurrencyConverterFormScreen from './CurrencyConverterFormScreen';
import { encryptPayload } from '../utils/encryption';

function Conversions() {
  const navigate = useNavigate();
  const [conversions, setConversions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [showdownload, setshowdownload] = useState(false);
  const [fromdate, setFromdate] = useState('');
  const [todate, setTodate] = useState('');
  const [downloadLoading, setdownloadLoading] = useState(false);
  const [downloadId, setdownloadId] = useState(0);
  const [hoveredConversion, setHoveredConversion] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [convertAgainData, setConvertAgainData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [convertAgainLoadingId, setConvertAgainLoadingId] = useState(null);

  const pageRef = useRef(0);

  const seedLastCurrenciesFromHistory = (conversionList) => {
    if (!conversionList || conversionList.length === 0) return;
    try {
      const latest = conversionList[0];
      const meta = JSON.parse(latest.meta);
      if (meta.client_sell_currency && meta.client_sell_currency_id) {
        sessionStorage.setItem('last_sell_currency', JSON.stringify({
          currency:   meta.client_sell_currency,
          currencyid: meta.client_sell_currency_id,
          flag:       meta.client_sell_flag || '',
        }));
      }
      if (meta.client_buy_currency && meta.client_buy_currency_id) {
        sessionStorage.setItem('last_buy_currency', JSON.stringify({
          currency:   meta.client_buy_currency,
          currencyid: meta.client_buy_currency_id,
          flag:       meta.client_buy_flag || '',
        }));
      }
    } catch (e) {
      console.warn('Could not seed last currencies from history:', e);
    }
  };

  const getData = async () => {
    if (loading || !hasMore) return;
    const pageNumber = pageRef.current;
    const login_id = sessionStorage.getItem('login_id');
    const login_token = sessionStorage.getItem('login_token');
    const login_workspaces_id = sessionStorage.getItem('login_workspaces_id');

    if (!login_id || !login_token) {
      sessionStorage.clear();
      navigate('/business');
      return;
    }

    setLoading(true);

    const from =
      new Date().getFullYear() + '-' + (new Date().getMonth() + 1) + '-' + (new Date().getDate() - 100);
    const to =
      new Date().getFullYear() + '-' + (new Date().getMonth() + 1) + '-' + new Date().getDate();

    try {
      const response = await axios.get(
        `${Constants.BASE_URL}API-FX-180-CONVERSIONLIST/${login_workspaces_id}?page=${pageNumber}&from=${from}&to=${to}`,
        {
          headers: {
            Authorization: 'Bearer ' + JSON.parse(login_token),
            fx_key: Constants.SUBSCRIPTION_KEY,
          },
        },
      );

      const newConversions = response.data.data || [];
      if (newConversions.length === 0) {
        setHasMore(false);
        setLoading(false);
        return;
      }

      setConversions((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const filtered = newConversions.filter((item) => !existingIds.has(item.id));
        const updated = [...prev, ...filtered];
        if (pageNumber === 0) seedLastCurrenciesFromHistory(updated);
        return updated;
      });
      pageRef.current = pageRef.current + 1;
    } catch (err) {
      console.error('Error fetching data:', err);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const onConvertAgainApplied = () => {
    setConvertAgainLoadingId(null);
  };

  const downloadStatement = async () => {
    if (!fromdate || !todate) {
      toast.error('Please select both From Date and To Date');
      return;
    }
    const from = new Date(fromdate);
    const to   = new Date(todate);
    if (isNaN(from) || isNaN(to)) {
      toast.error('Invalid date format. Use MM/DD/YYYY');
      return;
    }
    if (from > to) {
      toast.error('From Date cannot be after To Date');
      return;
    }
    try {
      setdownloadLoading(true);
      const login_workspaces_id = sessionStorage.getItem('login_workspaces_id');
      const encryptedPayload = await encryptPayload({ start_date: fromdate, end_date: todate, workspace_id: login_workspaces_id,platform: 'web',extraParamValidation: 'trustedfromweb' });
      const response = await axios.post(
        `${Constants.BASE_URL}API-FX-206-DOWNLOAD-TRADE-STATEMENT`,
        encryptedPayload,
        { headers: { 'Content-Type': 'application/json', fx_key: Constants.SUBSCRIPTION_KEY } },
      );
      window.open(JSON.parse(response.data.document).url, '_blank');
      setdownloadLoading(false);
      setshowdownload(false);
      setFromdate('')
      setTodate('')
    } catch (err) {
      setdownloadLoading(false);
      toast.error('Error downloading statement');
      console.error(err.response);
    }
  };

  const downloadSingleTrade = async (id, event) => {
    event.stopPropagation();
    setdownloadId(id);
    const login_workspaces_id = sessionStorage.getItem('login_workspaces_id');
    await axios
      .post(
        Constants.BASE_URL + 'API-FX-205-DOWNLOAD-SINGLE-TRADE',
        { id, workspace_id: login_workspaces_id },
        { headers: { 'Content-Type': 'application/json', fx_key: Constants.SUBSCRIPTION_KEY } },
      )
      .then((resp) => {
        window.open(JSON.parse(resp.data.document).url, '_blank');
        setdownloadId(0);
      })
      .catch((err) => {
        setdownloadId(0);
        console.log(err.response);
      });
  };

  const refreshConversions = async () => {
    const login_id = sessionStorage.getItem('login_id');
    const login_token = sessionStorage.getItem('login_token');
    const login_workspaces_id = sessionStorage.getItem('login_workspaces_id');
    if (!login_id || !login_token) {
      sessionStorage.clear();
      navigate('/business');
      return;
    }
    setLoading(true);
    const from =
      new Date().getFullYear() + '-' + (new Date().getMonth() + 1) + '-' + (new Date().getDate() - 100);
    const to =
      new Date().getFullYear() + '-' + (new Date().getMonth() + 1) + '-' + new Date().getDate();
    try {
      const response = await axios.get(
        `${Constants.BASE_URL}API-FX-180-CONVERSIONLIST/${login_workspaces_id}?page=0&from=${from}&to=${to}`,
        {
          headers: {
            Authorization: 'Bearer ' + JSON.parse(login_token),
            fx_key: Constants.SUBSCRIPTION_KEY,
          },
        },
      );
      const newConversions = response.data.data || [];
      setConversions(newConversions);
      pageRef.current = 1;
      setHasMore(newConversions.length > 0);
    } catch (err) {
      console.error('Error refreshing conversions:', err);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
  };

  const handleConvertAgain = (conversion, event) => {
    event.stopPropagation();
    const meta = JSON.parse(conversion.meta);
    // console.log("convert again data",meta)

    const newData = {
      sellCurrency:   meta.client_sell_currency,
      buyCurrency:    meta.client_buy_currency,
      sellAmount:     meta.client_sell_amount,
      buyAmount: meta.client_buy_amount,
      sellCurrencyId: meta.currency_sell,
      buyCurrencyId:  meta.currency_buy,
      sellFlag:       meta.client_sell_flag,
      buyFlag:        meta.client_buy_flag,
      trigger:Date.now(),
    };

    setConvertAgainLoadingId(conversion.id);

    // Reset then re-set so the useEffect in the form always fires,
    // even if the same row is clicked twice
    setConvertAgainData(null);
    setTimeout(() => setConvertAgainData(newData), 0);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredConversions = conversions.filter((conversion) => {
    if (!searchText) return true;
    const meta = JSON.parse(conversion.meta);
    const reference    = (meta.conversion_short_reference ?? '').toLowerCase();
    const sellCurrency = (meta.client_sell_currency ?? '').toLowerCase();
    const buyCurrency  = (meta.client_buy_currency  ?? '').toLowerCase();
    const search = searchText.toLowerCase();
    return reference.includes(search) || sellCurrency.includes(search) || buyCurrency.includes(search);
  });

  const today = new Date().toISOString().split('T')[0];

  return (
    <div
      className="bg-gradient-to-b from-[#f8fafc] via-[#eff6ff4d] to-[#eef2ff66]"
      style={{ minHeight: '100vh', padding: '48px 32px 24px 32px', display: 'flex', justifyContent: 'center' }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      <div style={{ width: '995px' }}>
        <div style={{ marginBottom: '24px' }}>
          <CurrencyConverterFormScreen
            conversions={conversions}
            refreshConversions={refreshConversions}
            convertAgainData={convertAgainData}
            onConvertAgainApplied={onConvertAgainApplied}
          />
        </div>

        <div style={{ background: '#fff', borderRadius: '12px', padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', gap: '16px' }}>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#1D2338', minWidth: '180px' }}>
              Conversion History
            </div>

            <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <input
                type="text"
                placeholder="Search By Reference Number Or Currency"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: '320px', fontSize: '14px', padding: '8px 12px', border: '1px solid #E6E5E5', borderRadius: '6px' }}
              />
            </div>

            <div style={{  minWidth: downloadLoading ? '470px' : (showdownload ? '400px' : '240px'), display: 'flex', justifyContent: 'flex-start' }}>
              {!showdownload ? (
                <button
                  onClick={() => setshowdownload(true)}
                  style={{ background: '#ea580c', color: '#fff', padding: '8px 14px', borderRadius: '6px', border: 'none', whiteSpace: 'nowrap' }}
                >
                  ↓ Download Statement
                </button>
              ) : (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input type="date" max={today} value={fromdate} onChange={(e) => setFromdate(e.target.value)} />
                  <span>to</span>
                  <input type="date" min={fromdate} max={today} value={todate} onChange={(e) => setTodate(e.target.value)} />
                  <button style = {{ background: '#ea580c', color: '#fff', padding: '8px 14px', borderRadius: '6px', border: 'none', whiteSpace: 'nowrap' }}  onClick={downloadStatement}>{downloadLoading ? 'Downloading...' : 'Download'}</button>
                </div>
              )}
            </div>
          </div>

          <InfiniteScroll
            dataLength={filteredConversions.length}
            next={() => getData()}
            height={400}
            hasMore={hasMore}
            loader={<p style={{ textAlign: 'center' }}>Loading...</p>}
          >
            {(showAll ? filteredConversions : filteredConversions.slice(0, 3)).map((conversion) => {
              const meta               = JSON.parse(conversion.meta);
              const reference          = meta.conversion_short_reference ?? 'N/A';
              const clientSell         = meta.client_sell_amount ?? '0';
              const clientBuy          = meta.client_buy_amount  ?? '0';
              const status             = meta.status ?? 'N/A';
              const createdDate        = meta.conversion_settlement_date;
              const clientSellCurrency = meta.client_sell_currency;
              const clientBuyCurrency  = meta.client_buy_currency;

              const isSettled           = status === 'trade_settled';
              const isPending           = status === 'pending';
              const isHovered           = hoveredConversion === conversion.id;
              const isConvertAgainLoading = convertAgainLoadingId === conversion.id;

              return (
                <div
                  key={conversion.id}
                  onMouseEnter={() => setHoveredConversion(conversion.id)}
                  onMouseLeave={() => setHoveredConversion(null)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px', borderBottom: '1px solid #E6E5E5' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: isSettled ? '#dcfce7' : isPending ? '#fee2e2' : '#fef3c7' }}>
                      {isSettled ? (
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-green-100 text-green-600 text-lg">⇄</span>
                      ) : isPending ? (
                        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-red-100 text-red-500 text-base font-bold">!</span>
                      ) : '⏳'}
                    </div>

                    <div>
                      <div style={{ fontWeight: '600' }}>
                        <span style={{ color: '#6b7280' }}>{clientSellCurrency} - {clientBuyCurrency}</span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                        {createdDate ? `${formatDate(createdDate)} ${formatTime(createdDate)}` : 'Pending'}
                      </div>
                      <div style={{ marginTop: '4px', fontSize: '12px', background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px', display: 'inline-block' }}>
                        Ref ID: {reference}
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {(isHovered || isConvertAgainLoading) && (
                      <button
                        onClick={(e) => {
                          if (isConvertAgainLoading) return;
                          handleConvertAgain(conversion, e);
                        }}
                        disabled={isConvertAgainLoading}
                        style={{
                          background:      isConvertAgainLoading ? '#4b5563' : 'rgb(11,33,78)',
                          color:           '#fff',
                          padding:         '6px 10px',
                          borderRadius:    '6px',
                          border:          'none',
                          cursor:          isConvertAgainLoading ? 'not-allowed' : 'pointer',
                          display:         'flex',
                          alignItems:      'center',
                          gap:             '6px',
                          minWidth:        '130px',
                          justifyContent:  'center',
                          opacity:         isConvertAgainLoading ? 0.8 : 1,
                        }}
                      >
                        {isConvertAgainLoading ? (
                          <>
                            <svg style={{ animation: 'spin 1s linear infinite', width: '14px', height: '14px', flexShrink: 0 }} viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="4" style={{ opacity: 0.25 }} />
                              <path fill="white" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" style={{ opacity: 0.75 }} />
                            </svg>
                            Processing...
                          </>
                        ) : 'Convert Again'}
                      </button>
                    )}

                    <div className="p-2" style={{ textAlign: 'right' }}>
                      {getCurrencySymbol(clientSellCurrency)} {parseFloat(clientSell).toFixed(2)} -{' '}
                      {getCurrencySymbol(clientBuyCurrency)} {parseFloat(clientBuy).toFixed(2)}
                      <div style={{ fontSize: '11px', marginTop: '4px', background: isSettled ? '#dcfce7' : isPending ? '#fee2e2' : '#fef3c7', color: isSettled ? '#16a34a' : isPending ? '#dc2626' : '#f59e0b', padding: '2px 6px', borderRadius: '4px' }}>
                        {isSettled ? 'Trade Settled' : isPending ? 'Pending' : status}
                      </div>
                    </div>

                    <button
                      onClick={(e) => downloadSingleTrade(conversion.id, e)}
                      style={{ border: '1px solid #d1d5db', background: '#f3f4f6', borderRadius: '6px', padding: '6px' }}
                    >
                      {downloadId === conversion.id ? <MdDownloading size={16} /> : <HiOutlineArrowDownTray size={16} />}
                    </button>
                  </div>
                </div>
              );
            })}
          </InfiniteScroll>

          <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

          {!showAll && filteredConversions.length > 3 && (
            <div style={{ textAlign: 'right', marginTop: '12px' }}>
              <button onClick={() => setShowAll(true)} style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer' }}>
                View All
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Conversions;